import { t } from "i18next";
import React, { useCallback, useEffect, useState } from "react";

import { ISortingGrade } from "../../utils/data-types";
import { monthString } from "../../utils/functions";
import DropdownMenu from "../dropdown/DropdownMenu";
import { Text } from "../text/Text";
import "./WasteAmount.scss";
import { WasteAmountBarChart } from "./recharts/WasteAmountBarChart";

interface WasteAmountProps {
  dataYear1: ISortingGrade[] | undefined;
  dataYear2: ISortingGrade[] | undefined;
  handleFirstSelect: (year: number) => void;
  handleSecondSelect: (year: number | undefined) => void;
  firstSelectedYear: number;
  secondSelectedYear: number | undefined;
  compareViewActive: boolean;
  setCompareViewActive: (active: boolean) => void;
  isMonth: boolean;
  setIsMonth: (month: boolean) => void;
  selectedMonth: number;
  setSelectedMonth: (month: number) => void;
}

export const WasteAmount = ({
  dataYear1,
  dataYear2,
  handleFirstSelect,
  handleSecondSelect,
  firstSelectedYear,
  secondSelectedYear,
  compareViewActive,
  setCompareViewActive,
  isMonth,
  setIsMonth,
  selectedMonth,
  setSelectedMonth,
}: WasteAmountProps) => {
  const currentYear = new Date().getFullYear();

  const [year1data, setYear1Data] = useState<ISortingGrade[]>(dataYear1 || []);
  const [year2data, setYear2Data] = useState<ISortingGrade[]>(dataYear2 || []);

  useEffect(() => {
    setYear1Data(dataYear1 || []);
    setYear2Data(dataYear2 || []);
  }, [dataYear1, dataYear2]);

  const handleSetSingleView = useCallback(() => {
    if (compareViewActive) {
      setCompareViewActive(false);
    }
    handleFirstSelect(secondSelectedYear || firstSelectedYear);
    handleSecondSelect(undefined);
  }, [
    compareViewActive,
    handleFirstSelect,
    secondSelectedYear,
    firstSelectedYear,
    handleSecondSelect,
    setCompareViewActive,
  ]);

  const handleSetCompareView = useCallback(() => {
    if (!compareViewActive) {
      setCompareViewActive(true);
    }

    handleFirstSelect(
      firstSelectedYear === currentYear ? currentYear - 1 : firstSelectedYear
    );
    handleSecondSelect(firstSelectedYear);
  }, [
    compareViewActive,
    currentYear,
    firstSelectedYear,
    handleFirstSelect,
    handleSecondSelect,
    setCompareViewActive,
  ]);

  const handleSetPeriod = useCallback(() => {
    setIsMonth(!isMonth);
  }, [setIsMonth, isMonth]);

  const yearsOptions = Array.from(
    { length: 5 },
    (_, index) => currentYear - index
  );

  return (
    <div className="waste-amount">
      <div className="waste-amount__header">
        <Text
          text={t("pages.customersInfo.wasteAmount")}
          size="xs"
          color="dark"
          fontWeight={600}
        />
        <div className="waste-amount__header__data-view">
          <Text
            text={t("pages.customersInfo.dataView")}
            size="xxs"
            fontWeight={500}
            color="dark-contrast"
          />
          <button
            className={`btn data-view ${!compareViewActive ? "active" : ""}`}
            onClick={handleSetSingleView}
          >
            {t("pages.customersInfo.singleView")}
          </button>
          <button
            className={`btn data-view ${compareViewActive ? "active" : ""}`}
            onClick={handleSetCompareView}
          >
            {t("pages.customersInfo.compareView")}
          </button>
        </div>
      </div>

      <div className="waste-amount__content">
        <div className="table-header">
          <div className="table-header__btn-container period">
            <button
              className={`btn period-btn ${!isMonth ? "active" : ""}`}
              onClick={handleSetPeriod}
            >
              {t("common.year")}
            </button>
            <button
              className={`btn period-btn ${isMonth ? "active" : ""}`}
              onClick={handleSetPeriod}
            >
              {t("common.month")}
            </button>
          </div>
          {isMonth && (
            <div className="table-header__btn-container month">
              {Array.from({ length: 12 }, (_, monthIndex) => (
                <button
                  key={monthIndex}
                  className={`btn period-btn month ${
                    selectedMonth === monthIndex + 1 ? "active" : ""
                  }`}
                  onClick={() => setSelectedMonth(monthIndex + 1)}
                >
                  {new Date(0, monthIndex).toLocaleString("no-NO", {
                    month: "short",
                  })}
                </button>
              ))}
            </div>
          )}
          <div className="table-header__description">
            <Text
              text={`${
                isMonth && compareViewActive
                  ? t("pages.customersInfo.wasteAmountDescCompare") +
                    monthString(selectedMonth) +
                    " i "
                  : isMonth && !compareViewActive
                  ? t("pages.customersInfo.wasteAmountDesc") +
                    monthString(selectedMonth) +
                    " i "
                  : !isMonth && compareViewActive
                  ? t("pages.customersInfo.wasteAmountDescCompare")
                  : t("pages.customersInfo.wasteAmountDesc")
              }`}
              size="xxs"
            />
            <DropdownMenu
              options={yearsOptions}
              selectedOption={firstSelectedYear}
              onSelectOption={handleFirstSelect}
              disableCurrentYear={compareViewActive}
            />
            {compareViewActive && (
              <>
                {"og"}
                <DropdownMenu
                  options={yearsOptions}
                  selectedOption={secondSelectedYear}
                  onSelectOption={handleSecondSelect}
                  disableFirstYear={compareViewActive}
                />
              </>
            )}
          </div>
        </div>
        <div className="waste-amount__content__chart">
          <div className="waste-amount__content__chart__item">
            <div className="waste-amount__content__chart__item__chart">
              <WasteAmountBarChart
                dataYear1={year1data}
                dataYear2={year2data}
                year1={firstSelectedYear}
                year2={secondSelectedYear}
                diagramView={!compareViewActive ? "single" : "compare"}
                month={selectedMonth}
                monthlyView={isMonth}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
